import { Route } from 'vue-router';
import Vue, { Component } from 'vue';
import { ComponentOptions } from 'vue/types/umd';
import _ from '.';

/* eslint-disable no-underscore-dangle */
export default class ComponentHelpers {
  /** Gets a unique id to identify exactly where the element is within the UI structure */
  getComponentUILocationId(vm: Vue | HTMLElement | Node | ComponentOptions<any>) {
    let current = vm as Vue | HTMLElement | undefined | null;
    let uiPath = '';
    const getParent = (obj: any) => obj?.$parent ?? obj?.parentElement;
    while (current) {
      let uniqueName = '';
      if ('$vnode' in current && current.$vnode) {
        const tagName = current.$vnode.tag;
        const index = current.$parent?.$children?.indexOf(current, 0) ?? 0;
        uniqueName = `${tagName}_${index}`;
        current = getParent(current);
      } else {
        const el = _.getElement(current);
        const {
          tagName, className, id, parentElement,
        } = el;
        const children = parentElement?.children;
        let indexOfEl = 0;
        if (children) {
          indexOfEl = Array.prototype.indexOf.call(children, current, 0);
        }
        uniqueName = `${tagName}_${className}_${id}_${indexOfEl}_`;
        current = getParent(current);
      }
      if (!getParent(current)) {
        // Discard the last node as this is mostly the app wrapper and changes a lot
        current = undefined;
      }
      uiPath += uniqueName;
    }
    return _.hash(uiPath);
  }
  /**
   * Gets the root element of the Vue App, this is essential if the app is run in an embedded scenario
   */
  getAppEl() {
    try {
      // TODO: This breaks the test, so it is being mocked, needs a more solid concept in future
      if (Vue.$version.buildConfiguration === 'test') {
        return document.createElement('div');
      }
      const rootEl = document.getElementById(Vue.$appId);
      const appEl = rootEl!.getElementsByClassName('p-app')[0];

      return appEl as Element | HTMLElement;
    } catch (error) {
      Vue.$log.error(`Unable to retrieve app node: ${error}`);
    }
    return {} as Element | HTMLElement;
  }

  /** Get slot VNode, props can be passed to scoped slots (ignored for normals slots) */
  getSlot(vm: Vue, name: string, props?: any) {
    if (vm === undefined) {
      return undefined;
    }
    const nodes = vm.$slots[name] ?? [];
    const [slotNode] = nodes;
    if (slotNode) {
      return slotNode;
    }
    const scopedSlot = vm.$scopedSlots[name];
    if (scopedSlot && typeof scopedSlot === 'function') {
      const [scopedSlotNode] = scopedSlot(props) ?? [];
      return scopedSlotNode;
    }
    return undefined;
  }

  /**
   * Checks if the vm has a slot with a specific name
   * @param vm The vm to be tested (this is usually the current instance "this")
   * @param name The slot name
   */
  hasSlot(vm: Vue | undefined, name: string) {
    if (!_.isSet(vm)) {
      return false;
    }
    return vm.$slots[name] !== undefined || vm.$scopedSlots[name] !== undefined;
  }

  // TODO: Move to somewhere more suitable
  classify(str: string) {
    return str
      .replace(/(?:^|[-_])(\w)/g, (c) => c.toUpperCase())
      .replace(/[-_]/g, '');
  }

  formatName(vm: Vue) {
    if (vm === undefined) {
      return '<Undefined>';
    }
    if (vm.$root === vm) {
      return '<Root>';
    }
    // eslint-disable-next-line no-nested-ternary
    const options = typeof vm === 'function' && (vm as any).cid != null
      ? (vm as any).options
      : (vm as any)._isVue
        ? vm.$options || (vm as any).constructor.options
        : vm || {};
    let name = options.name || options._componentTag;

    const file = options.__file;
    if (!name && file) {
      const match = file.match(/([^/\\]+)\.vue$/);
      name = match && match[1];
    }
    return (
      (name ? `<${this.classify(name)}>` : '<Anonymous>')
        + (file ? ` at ${file}` : '')
    );
  }
  generateTrace(vm: Vue): string {
    if (vm === undefined) {
      return 'vm was undefined';
    }
    if (vm.$parent) {
      const tree: Vue[] = [];
      let currentRecursiveSequence = 0;
      while (vm) {
        if (tree.length > 0) {
          const last = tree[tree.length - 1];
          if (last.constructor === vm.constructor) {
            currentRecursiveSequence += 1;
            vm = vm.$parent as any;
            continue;
          } else if (currentRecursiveSequence > 0) {
            tree[tree.length - 1] = last;// [last, currentRecursiveSequence];
            currentRecursiveSequence = 0;
          }
        }
        tree.push(vm);
        vm = vm.$parent as any;
      }
      return `\n\nfound in\n\n${tree
        .map((e, i) => `${
          i === 0 ? '---> ' : ' '.repeat(5 + i * 2)
        }${
          Array.isArray(e)
            ? `${this.formatName(e[0])}... (${e[1]} recursive calls)`
            : this.formatName(e)
        }`)
        .join('\n')}`;
    }
    return `\n\n(found in ${this.formatName(vm)})`;
  }

  /**
   * @deprecated Use the RouterUtils plugin instead ```Vue.$routerUtils.getComponentsForRoute```
   * Returns and resolves matched components from a route
   */
  async getComponentsForRoute(route: Route) {
    return Vue.$routerUtils.getComponentsForRoute(route);
  }

  /**
   * @deprecated Use the RouterUtils plugin instead ```Vue.$routerUtils.getRouteOption```
   * Returns the first option for the route in a matched component
   */
  async getRouteOption<T>(route: Route, key: string): Promise<T | undefined> {
    return Vue.$routerUtils.getRouteOption(route, key);
  }
}
